import { usePlans } from '@/hooks/usePlans';
import { useClickref } from '@/hooks/useClickref';
import { PLAN_TYPES, type PlanType } from '@/config/api';
import { decodeHtmlEntities } from '@/utils/decodeHtmlEntities';
import './FeaturedCard.scss';
import bulletCheckIcon from '@/assets/icons/bullet-check.svg';
import infiniIcon from '@/assets/icons/infini.svg';
import dataIcon from '@/assets/icons/data.svg';
import iconGaugeSpeed from '@/assets/icons/icon-gauge-speed.svg';
import signalIcon from '@/assets/icons/signal.svg';
import iconTv from '@/assets/icons/icon-tv.svg';
import chainsIcon from '@/assets/icons/chains.svg';
import { formatPrice } from '@/utils/formatPrice';

export interface FeaturedCardProps {
    planType?: PlanType;
    planId?: string;
    showAnnonce?: boolean;
    position?: number;
}

function slugify(text: string): string {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

export function FeaturedCard({ planType = PLAN_TYPES.MOBILE, planId, showAnnonce = true, position = 1 }: FeaturedCardProps) {
    const { plans } = usePlans(planType);
    const { getClickrefUrl, handleClickrefLink } = useClickref();
    const plan = planId
        ? plans.find(p => p.id === planId) ?? null
        : null;

    if (!plan) {
        return null;
    }

    const { operator, offer, name, visible_infos, extra } = plan;
    const price = offer.price.price;
    const crossedPrice = offer.price.crossed_price;
    const rawUrl = offer.url_lemon;
    const ctaUrl = getClickrefUrl(rawUrl);

    const isInternet = planType === PLAN_TYPES.INTERNET;
    const desktopIcons = isInternet
        ? [iconGaugeSpeed, signalIcon, iconTv, chainsIcon]
        : [infiniIcon, dataIcon, dataIcon, dataIcon];

    const items: string[] = isInternet
        ? (() => {
            const base = visible_infos.slice(0, 3);
            const engagement = extra.engagement_duration;
            if (!engagement || engagement === 0) {
                base.push('Sans engagement');
            } else if (offer.offer_duration && offer.offer_duration !== '') {
                base.push(offer.offer_duration);
            }
            return base;
        })()
        : visible_infos.slice(0, 4);

    return (
        <a
            href={ctaUrl}
            onClick={(e) => handleClickrefLink(e, rawUrl)}
            target="_blank"
            rel="noopener nofollow noreferrer"
            className="no-underline"
            data-gtm-offer-type="comparator_link"
            data-gtm-bloc-type="featured"
            data-gtm-offer-url={ctaUrl}
            data-gtm-seller={operator.slug}
            data-gtm-product-name={slugify(name)}
            data-gtm-position-marchand={position}
            data-gtm-product-category={planType}
            data-gtm-zone="comparator_featured"
            data-gtm-position={position}
        >
            <div className={`featured-card${isInternet ? ' featured-card--internet' : ''}`}>
                <div className="featured-card__offert">
                    <div className="featured-card__offert-top">
                        <div className="featured-card__offert-logo-group">
                            <div className="featured-card__offert-top-logo">
                                <img
                                    src={operator.logo}
                                    alt={operator.name}
                                    width={44}
                                    height={44}
                                />
                            </div>
                            <span className="featured-card__offert-tag-tablet">Annonce</span>
                        </div>
                        <div className="featured-card__offert-price">
                            <div className="featured-card__offert-price-current">
                                <span className="featured-card__offert-price-current-value">{formatPrice(price)} €</span>
                                <span className="featured-card__offert-price-current-period">/mois</span>
                            </div>
                            {crossedPrice && (
                                <span className="featured-card__offert-price-crossed">
                                    {formatPrice(crossedPrice)}€/mois
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="featured-card__offert-bottom">
                        <div className="featured-card__offert-bottom-title">{decodeHtmlEntities(name)}</div>
                        {showAnnonce && <span className="featured-card__offert-bottom-tag">Annonce</span>}
                    </div>
                </div>
                <div className="featured-card__details">
                    <div className="featured-card__details-list">
                        <ul>
                            {items.map((info, index) => (
                                <li key={index}>
                                    <img
                                        src={desktopIcons[index]}
                                        alt=""
                                        width={24}
                                        height={24}
                                        className="featured-card__details-icon featured-card__details-icon--desktop"
                                    />
                                    <img
                                        src={bulletCheckIcon}
                                        alt=""
                                        width={24}
                                        height={24}
                                        className="featured-card__details-icon featured-card__details-icon--mobile"
                                    />
                                    <div className="featured-card__details-info">
                                        <span className="featured-card__details-info-text" dangerouslySetInnerHTML={{ __html: info }} />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <span className="featured-card__details-link">
                        Voir l&apos;offre
                    </span>
                </div>
            </div>
        </a>
    );
}

export default FeaturedCard;
